import { createContext, useContext, useMemo, type ReactNode } from 'react'
import type { Permission, ModuleConfig, RouteConfig } from './types'

export interface AuthUser {
  uid: number | string
  email?: string
  name?: string
  /** Resolved permission keys for this session. */
  permissions: ReadonlySet<Permission>
  /**
   * Sprint 16 — true if the session's role scopes data to a subset of
   * customers (outside_sales_rep / inside_sales_rep). Other roles see
   * everything; chip stays hidden.
   */
  scoped?: boolean
  /**
   * Sprint 16 — number of customers in the rep's scope. Only set when
   * `scoped` is true. Drives the "Showing your customers (N)" chip.
   */
  scopeCount?: number | null
  /**
   * 2026-07-21 — ISO date floor for the Order History view (currently
   * only set for outside_sales_rep, `rbac.py`'s `order_history_floor`).
   * Undefined for every role without a floor.
   */
  orderHistoryFloor?: string
  /**
   * Connect-As — admin's identity when this session is impersonating a
   * user. Null when the session is the user's own. Drives the
   * shell-wide ImpersonationBanner + hides the Connect-As button on
   * Settings rows.
   */
  impersonating?: {
    adminEmail: string
    adminName: string
    startedAt: number
  } | null
}

export const AuthContext = createContext<AuthUser | null>(null)

/**
 * Dev stub: grants every base permission. Replace by the real gateway
 * handshake at M6 alongside Open Orders (JWT payload → permissions).
 */
export const DEV_AUTH_USER: AuthUser = {
  uid: 2,
  email: 'cnorris@siliconesplus.com',
  name: 'Chris Norris',
  permissions: new Set<Permission>([
    'admin',
    'sales',
    'operations',
    'regulatory',
    'finance',
    'viewer',
  ]),
}

export function AuthProvider({
  user,
  children,
}: {
  user: AuthUser
  children: ReactNode
}) {
  return <AuthContext.Provider value={user}>{children}</AuthContext.Provider>
}

export function useAuth(): AuthUser {
  const user = useContext(AuthContext)
  if (!user) {
    throw new Error('useAuth must be used inside <AuthProvider>')
  }
  return user
}

/** True if the current user holds any of the listed permissions. */
export function useHasAnyPermission(required: readonly Permission[]): boolean {
  const user = useAuth()
  return useMemo(
    () => required.some((p) => user.permissions.has(p)),
    [user, required],
  )
}

/** True if the current user holds the given single permission. */
export function usePermission(key: Permission): boolean {
  return useHasAnyPermission([key])
}

/** Pure helper — useful outside React (route guards, registry filtering). */
export function hasAnyPermission(
  user: AuthUser,
  required: readonly Permission[],
): boolean {
  return required.some((p) => user.permissions.has(p))
}

/** Gate a module: requires any of its module-level permissions. */
export function canAccessModule(user: AuthUser, module: ModuleConfig): boolean {
  if (module.permissions.length === 0) return true
  return hasAnyPermission(user, module.permissions)
}

/** Gate a route: module-level + route-level permissions both apply. */
export function canAccessRoute(
  user: AuthUser,
  module: ModuleConfig,
  route: RouteConfig,
): boolean {
  if (!canAccessModule(user, module)) return false
  if (!route.permissions || route.permissions.length === 0) return true
  return hasAnyPermission(user, route.permissions)
}
